import React, { useEffect, useRef, useState } from 'react';
import API from '../../../api/api';
import { SceneGroup } from './MediaEditorSection.helpers';
import { useToast } from '../../../hooks/useToast';

interface TransitionChipProps {
  scene: SceneGroup;
  onRefresh: () => void;
}

const TRANSITION_OPTIONS = [
  { value: 'fade', label: 'Fade' },
  { value: 'zoom-in', label: 'Zoom In' },
  { value: 'zoom-out', label: 'Zoom Out' },
  { value: 'slow-zoom-in', label: 'Slow Zoom In' },
  { value: 'slow-zoom-out', label: 'Slow Zoom Out' },
];

const MediaEditorTransitionChip: React.FC<TransitionChipProps> = ({ scene, onRefresh }) => {
  const toast = useToast();
  const [showPicker, setShowPicker] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const primaryItem = scene.video || scene.image || scene.allItems[0];
  const transition = primaryItem?.metadata?.transition_out as string | undefined;
  const label = TRANSITION_OPTIONS.find((t) => t.value === transition)?.label;

  useEffect(() => {
    if (!showPicker) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setShowPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPicker]);

  const handleSelect = async (value: string) => {
    if (!primaryItem) return;
    setShowPicker(false);
    try {
      await API.editMediaMetadata(primaryItem.id, 'transition_out', value);
      onRefresh();
    } catch (error: any) {
      toast({ text: `Failed to set transition: ${error.message}`, level: 'error' });
    }
  };

  const handleRemove = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!primaryItem) return;
    try {
      await API.editMediaMetadata(primaryItem.id, 'transition_out', null);
      onRefresh();
    } catch (error: any) {
      toast({ text: `Failed to remove transition: ${error.message}`, level: 'error' });
    }
  };

  return (
    <div ref={ref} className="relative flex items-center shrink-0 self-stretch">
      {transition && label ? (
        <div className="flex flex-col items-center gap-0.5 px-0.5">
          <span className="text-[8px] text-zinc-600 font-mono uppercase tracking-wider border border-white/10 px-1 py-0.5 whitespace-nowrap">
            {label}
          </span>
          <button
            onClick={handleRemove}
            className="text-zinc-700 hover:text-zinc-300 font-mono text-[9px] transition-colors leading-none"
            title="Remove transition"
          >✕</button>
        </div>
      ) : (
        <button
          onClick={() => setShowPicker((v) => !v)}
          className="text-zinc-700 hover:text-zinc-400 font-mono text-xs transition-colors w-5 flex items-center justify-center"
          title="Add transition"
        >+</button>
      )}

      {showPicker && (
        <div className="absolute z-30 top-1/2 left-full -translate-y-1/2 ml-1 bg-zinc-950 border border-white/15 shadow-xl py-1 min-w-[120px]">
          {TRANSITION_OPTIONS.map((t) => (
            <button
              key={t.value}
              onClick={() => handleSelect(t.value)}
              className="w-full text-left px-3 py-1.5 text-[11px] font-mono text-zinc-400 hover:text-white hover:bg-white/5 transition-colors whitespace-nowrap"
            >
              {t.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default MediaEditorTransitionChip;
